import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './LandingPage.css';
import { Link } from 'react-router-dom';
import SubscriptionModal from './SubscriptionModal';
import ReactDOM from 'react-dom';
import { useUser } from '../contexts/UserContext';
import FlipCardExample from './FlipCardExample'; // adjust path as needed


// Helper function to return a random integer between min and max (inclusive)
const getRandomInitial = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const LandingPage = () => {
  // 1️⃣ Initialize from localStorage or random:
  const [resumeCount, setResumeCount] = useState(() => {
    const saved = localStorage.getItem('resumeCount')
    return saved !== null
      ? parseInt(saved, 10)
      : getRandomInitial(1000, 3000)
  });

  const [coverLetterCount, setCoverLetterCount] = useState(() => {
    const saved = localStorage.getItem('coverLetterCount')
    return saved !== null
      ? parseInt(saved, 10)
      : getRandomInitial(1200, 3700)
  });
  // Refs for resume odometer
  const odometerRef = useRef(null);
  const odometerInstanceRef = useRef(null);

  // Refs for cover letter odometer
  const coverLetterRef = useRef(null);
  const coverLetterInstanceRef = useRef(null);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const { onSignupOpen } = useUser();

  // Schedule resume counter increment at random intervals (between 3000ms and 10000ms)
  useEffect(() => {
    let timer;
    const scheduleIncrement = () => {
      const randomDelay = Math.floor(Math.random() * (10000 - 3000 + 1)) + 3000;
      timer = setTimeout(() => {
        setResumeCount(prev => prev + 1);
        scheduleIncrement();
      }, randomDelay);
    };
    scheduleIncrement();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const savedResume = localStorage.getItem('resumeCount');
    const savedCover  = localStorage.getItem('coverLetterCount');
    if (savedResume)      setResumeCount(+savedResume);
    if (savedCover)       setCoverLetterCount(+savedCover);
    // (else keep the getRandomInitial defaults)
  }, []);

  // Schedule cover letter counter increment ~2.7 times faster
  useEffect(() => {
    let coverTimer;
    const minDelay = Math.floor(3000 / 2.7);
    const maxDelay = Math.floor(10000 / 2.7);
    const scheduleCoverIncrement = () => {
      const randomDelayCover = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
      coverTimer = setTimeout(() => {
        setCoverLetterCount(prev => prev + 1);
        scheduleCoverIncrement();
      }, randomDelayCover);
    };
    scheduleCoverIncrement();
    return () => clearTimeout(coverTimer);
  }, []);

  // Initialize resume Odometer instance (once the component mounts)
  useEffect(() => {
    if (odometerRef.current && window.Odometer && !odometerInstanceRef.current) {
      odometerInstanceRef.current = new window.Odometer({
        el: odometerRef.current,
        value: resumeCount,
        format: "(,ddd)",
        duration: 500, // animation duration in milliseconds
      });
    }
  }, []);

  // Initialize cover letter Odometer instance
  useEffect(() => {
    if (coverLetterRef.current && window.Odometer && !coverLetterInstanceRef.current) {
      coverLetterInstanceRef.current = new window.Odometer({
        el: coverLetterRef.current,
        value: coverLetterCount,
        format: "(,ddd)",
        duration: 500,
      });
    }
  }, []);

  // Update the resume odometer whenever resumeCount changes
  useEffect(() => {
    if (odometerInstanceRef.current) {
      odometerInstanceRef.current.update(resumeCount);
    }
  }, [resumeCount]);

  // Update the cover letter odometer whenever coverLetterCount changes
  useEffect(() => {
    if (coverLetterInstanceRef.current) {
      coverLetterInstanceRef.current.update(coverLetterCount);
    }
  }, [coverLetterCount]);

    // 2️⃣ Persist on every change:
    useEffect(() => {
      localStorage.setItem('resumeCount', resumeCount.toString());
    }, [resumeCount]);
  
    useEffect(() => {
      localStorage.setItem('coverLetterCount', coverLetterCount.toString());
    }, [coverLetterCount]);

    // Instead of immediately triggering checkout, we open the subscription modal.
    const handleUpgradeClick = () => {
        setShowSubscriptionModal(true);
      };

    // For navbar upgrades, success should redirect to home.
    const handleSubscriptionSuccess = () => {
        window.location.href = "/";
    };

    // Opens the signup modal – your signup modal logic should handle this.
    const handleRequireSignup = () => {
        onSignupOpen();
        setShowSubscriptionModal(false);
    };

  return (
    <div className="landing-page">
      {/* Helmet loads the Odometer CSS and JS */}
      <Helmet>
        <link 
          rel="stylesheet" 
          href="https://cdnjs.cloudflare.com/ajax/libs/odometer.js/0.4.7/themes/odometer-theme-default.css" 
          integrity="sha512-kMPqFnKueEwgQFzXLEEl671aHhQqrZLS5IP3HzqdfozaST/EgU+/wkM07JCmXFAt9GO810I//8DBonsJUzGQsQ=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
        <script 
          src="https://cdnjs.cloudflare.com/ajax/libs/odometer.js/0.4.7/odometer.min.js"
          integrity="sha512-v3fZyWIk7kh9yGNQZf1SnSjIxjAKsYbg6UQ+B+QxAZqJQLrN3jMjrdNwcxV6tis6S0s1xyVDZrDz9UoRLfRpWw=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        ></script>
      </Helmet>

      <section className="hero">
        <div className="hero-text">
          <h1>PositionPrep: Your Ultimate AI-Powered Career Assistant</h1>
          <p>
            Transform your job search with a powerful suite of tools. Create professional resumes, generate personalized cover letters, and receive tailored interview preparation — all for just $12.99/month.
          </p>
            <Link to="/resume-builder">
                <button className="cta-button">Get Started Now</button>
            </Link>
        </div>
        <img src="/hero-white.png" alt="Hero Illustration" className="hero-image" />
      </section>

      <section className="stats">
        {/* Resume Counter */}
        <div className="counter">
          <div ref={odometerRef} className="counter-number odometer">
            {resumeCount}
          </div>
          <span>Resumes built today</span>
        </div>

        {/* Cover Letters Counter */}
        <div className="counter">
          <div ref={coverLetterRef} className="counter-number odometer">
            {coverLetterCount}
          </div>
          <span>Cover letters created today</span>
        </div>
      </section>


      {/* The rest of your landing page sections remain unchanged */}
      <section className="features">
        <h2>Why Choose PositionPrep?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <h3>AI-Powered Resume Builder</h3>
            <p>Build a stunning, ATS-friendly resume in minutes using our intelligent editor.</p>
          </div>
          <div className="feature-card">
            <h3>Cover Letter Generator</h3>
            <p>Create tailored, persuasive cover letters for every job with ease.</p>
          </div>
          <div className="feature-card">
            <h3>Tailored Interview Prep</h3>
            <p>Get role-specific interview questions and AI-generated answers to help you shine.</p>
          </div>
          <div className="feature-card">
            <h3>Affordable Pricing</h3>
            <p>Access everything for only $12.99/month – no hidden fees.</p>
          </div>
        </div>
      </section>

      <section className="testimonials-with-image">
        <div className="testimonial-image">
            <img src="/hired.png" alt="Dream Job Illustration" />
        </div>
        <div className="testimonials">
            <h2>What Our Users Say</h2>
            <p>"PositionPrep transformed my career. The personalized interview prep and cover letter generator helped me nail my dream job!" – Jamie D.</p>
            <p>"I landed an interview with a top company thanks to PositionPrep’s unique tools. It’s a game-changer!" – Alex K.</p>
        </div>
       </section>


      <section className="steps-section">
        <h2>Start in 3 Easy Steps</h2>
        <ol>
          <li><strong>Enter your details</strong> — Upload or build from scratch using smart guidance.</li>
          <li><strong>Customize</strong> — Select templates and adjust your tone for resume and cover letter.</li>
          <li><strong>Print or Export</strong> — Download your polished documents and prep with mock interview questions.</li>
        </ol>
        <Link to="/resume-builder">
            <button className="cta-button secondary">Create My Resume Now</button>
        </Link>
      </section>

      <section className="advantages-section">
        <h2>The Advantages of Using PositionPrep</h2>
        <div className="advantages-grid">
          <div className="feature-card">
            <h3>Save Valuable Time</h3>
            <p>Don’t start from scratch — get smart suggestions and structure instantly.</p>
          </div>
          <div className="feature-card">
            <h3>Build Accompanying Documents</h3>
            <p>Resume, cover letter, and interview prep — all in one place, seamlessly integrated.</p>
          </div>
          <div className="feature-card">
            <h3>Flexible Templates</h3>
            <p>Pick from modern or traditional designs that fit your career goals.</p>
          </div>
          <div className="feature-card">
            <h3>Data Protection</h3>
            <p>We encrypt and store your data securely. It’s never sold or shared.</p>
          </div>
          <div className="feature-card">
            <h3>Built by Experts</h3>
            <p>Our system is trained on feedback from recruiters, HR professionals, and hiring managers.</p>
          </div>
          <div className="feature-card">
            <h3>ATS-Friendly Output</h3>
            <p>Every resume is structured to beat ATS filters and get seen by hiring teams.</p>
          </div>
        </div>
      </section>

      <section className="interview-showcase">
  <div className="interview-spotlight-content">
    <h2>Ace Every Interview with Confidence</h2>
    <p className="intro">
      PositionPrep’s smart interview tool doesn’t just give you questions — it prepares you like a pro.
    </p>
    <ul className="benefits-list">
      <li>
        <strong>🔍 Role-Specific Insights:</strong> We analyze the job description and industry to tailor your prep.
      </li>
      <li>
        <strong>🧠 Expert Answers:</strong> We use your resume to craft intelligent, impressive responses that show off your experience.
      </li>
      <li>
        <strong>🎯 Sharpen Your Edge:</strong> Practice and revise with precision to boost your confidence and readiness.
      </li>
    </ul>
  </div>
  <div className="interactive-showcase">
    <h2>Try the Interview Question Generator</h2>
    <p>
      Click a card to flip it and see a sample of what our interview preparation tool can do.
    </p>
    <div className="flip-card-container">
      <FlipCardExample
        question="Can you tell me about a challenging project you've worked on?"
        answer="In my previous role, I led a project to migrate our entire user database to a new platform under tight deadlines. I started by..."
      />
      <FlipCardExample
        question="How do you prioritize tasks when juggling multiple deadlines?"
        answer="I use a priority matrix to quickly assess urgency and impact, ensuring high-value tasks get done first without sacrificing quality."
      />
      <FlipCardExample
        question="Describe a time you had to learn something quickly for a role."
        answer="At my last job, I was tasked with configuring a security system I hadn't used before. I enrolled in a crash course and deployed it within 48 hours."
      />
    </div>
  </div>
</section>




      <section className="cta">
        <h2>Ready to Elevate Your Career?</h2>
        <button
            className="cta-button"
            onClick={handleUpgradeClick}
            >Upgrade to Plus Now</button>
      </section>

      {/* Render Subscription Modal via Portal */}
      {showSubscriptionModal &&
        ReactDOM.createPortal(
          <div className="subscription-overlay">
            <div className="subscription-modal">
              <button
                className="modal-close"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
              <SubscriptionModal
                isOpen={true}
                onClose={() => setShowSubscriptionModal(false)}
                onSubscriptionSuccess={handleSubscriptionSuccess}
                onRequireSignup={handleRequireSignup}
                productType="navbar" // Pass the product type for navbar upgrades
              />
            </div>
          </div>,
          document.getElementById('modal-root')
        )
      }
    </div>
    
  );
};

export default LandingPage;
